import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
    import.meta.env.VITE_PUBLIC_SUPABASE_URL,
    import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export type ActivityType = 'quiz' | 'flashcards' | 'audio';

interface ActivityLogEntry {
    id: string;
    type: ActivityType;
    title: string;
    subtitle: string;
    date: string;
    value?: number | string;
    categoryId?: number;
}

export const logActivity = async (activity: {
    type: ActivityType,
    title: string,
    subtitle: string,
    value?: number | string,
    categoryId?: number
}) => {
    try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const metadata = user.user_metadata || {};
        const existingLog = [...(metadata.activity_log || [])];

        // Look for a similar recent activity (same type and category/title) to update it
        // instead of creating many separate entries for each question/card
        const existingIndex = existingLog.findIndex(a =>
            a.type === activity.type &&
            (activity.categoryId ? a.categoryId === activity.categoryId : a.title === activity.title) &&
            // Only update if it was within the last 30 minutes
            (Date.now() - new Date(a.date).getTime() < 30 * 60 * 1000)
        );

        let updatedLog;
        if (existingIndex > -1) {
            // Update existing entry
            updatedLog = [...existingLog];
            updatedLog[existingIndex] = {
                ...updatedLog[existingIndex],
                ...activity,
                date: new Date().toISOString()
            };
        } else {
            // Prepend new entry
            const newActivity: ActivityLogEntry = {
                id: `${activity.type}-${Date.now()}`,
                ...activity,
                date: new Date().toISOString(),
            };
            updatedLog = [newActivity, ...existingLog].slice(0, 20);
        }

        const activityTimestamps = metadata.activity_timestamps || {};
        const catKey = activity.categoryId ? `cat_${activity.categoryId}` : 'global';
        if (!activityTimestamps[catKey]) activityTimestamps[catKey] = {};
        activityTimestamps[catKey][activity.type] = new Date().toISOString();

        await supabase.auth.updateUser({
            data: {
                ...metadata,
                activity_log: updatedLog,
                activity_timestamps: activityTimestamps
            }
        });
    } catch (error) {
        console.error('Error logging activity:', error);
    }
};
